import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ChevronDown, ChevronUp } from 'lucide-react';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { formatPrizeLabel, type UserPrizeData } from '@/utils/prize';

export interface LeaderboardTableEntry {
  user_id: string;
  username: string;
  points: number;
  rank: number;
  prize?: UserPrizeData | null;
}

export interface LeaderboardTableProps {
  entries: LeaderboardTableEntry[];
  currentUserId?: string;
  isLoading?: boolean;
  error?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /**
   * When false, expand chevron is hidden and rows are not expandable
   * (e.g. other users before contest kickoff). Current user always expandable
   * when renderExpanded is provided.
   */
  canExpandEntry?: (entry: LeaderboardTableEntry, isCurrentUser: boolean) => boolean;
  /** Expanded panel under a row. Omit to hide chevrons. */
  renderExpanded?: (
    entry: LeaderboardTableEntry,
    isCurrentUser: boolean,
    isPinned: boolean
  ) => React.ReactNode;
  emptyTitle?: string;
  errorTitle?: string;
  loadingLabel?: string;
  className?: string;
  'data-test-id'?: string;
}

const ROW_GRID = 'grid grid-cols-[46px_minmax(0,1fr)_56px_56px_24px] gap-x-3 items-center px-2';

/**
 * Circus Daily leaderboard table — Figma Rank / Player / Prize / Points
 * Shared by contest gameplay and lobby aggregate leaderboard.
 */
export function LeaderboardTable({
  entries,
  currentUserId,
  isLoading = false,
  error = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  canExpandEntry = () => true,
  renderExpanded,
  emptyTitle,
  errorTitle,
  loadingLabel,
  className = '',
  'data-test-id': dataTestId = 'leaderboard-table',
}: LeaderboardTableProps) {
  const { t } = useTranslation();
  const resolvedEmptyTitle = emptyTitle ?? t('leaderboard.noData');
  const resolvedErrorTitle = errorTitle ?? t('leaderboard.failedToLoad');
  const resolvedLoadingLabel = loadingLabel ?? t('leaderboard.loading');

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const tableTopRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const scrollToTop = () => {
    tableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Among tied ranks, surface the current user first
  const orderedEntries = currentUserId
    ? [...entries].sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (a.user_id === currentUserId) return -1;
        if (b.user_id === currentUserId) return 1;
        return 0;
      })
    : entries;

  const currentUserEntry = currentUserId
    ? orderedEntries.find(entry => entry.user_id === currentUserId)
    : undefined;

  const renderRow = (
    entry: LeaderboardTableEntry,
    isCurrentUser: boolean,
    isPinned: boolean = false
  ) => {
    const canExpand = !!renderExpanded && canExpandEntry(entry, isCurrentUser);
    const expandKey = isPinned ? `${entry.user_id}-pinned` : entry.user_id;
    const isExpanded = canExpand && expandedKeys.has(expandKey);
    const prizeLabel = formatPrizeLabel(entry.prize);
    const isWinner = isCurrentUser && entry.rank === 1;
    const isHighlighted = isCurrentUser;

    const rowSurfaceClass = isWinner
      ? 'bg-primary border-primary border-t'
      : isHighlighted
        ? 'bg-white border-[#1a1c1e]/15 border-t'
        : 'bg-transparent border-border hover:bg-surface-muted/40';

    const primaryTextClass = isWinner
      ? 'text-white'
      : isHighlighted
        ? 'text-background'
        : 'text-white';
    const prizeTextClass = isWinner
      ? 'text-white'
      : isHighlighted
        ? 'text-background'
        : 'text-success';
    const chevronClass = isWinner
      ? 'text-white/70'
      : isHighlighted
        ? 'text-background/70'
        : 'text-muted-foreground';

    return (
      <div key={expandKey}>
        <button
          type="button"
          onClick={() => canExpand && toggleExpanded(expandKey)}
          disabled={!canExpand}
          data-test-id={`leaderboard-row-${entry.rank}${isPinned ? '-pinned' : ''}`}
          data-test-user-id={entry.user_id}
          data-test-state={isWinner ? 'winner' : isCurrentUser ? 'current-user' : 'other-user'}
          className={`${ROW_GRID} w-full py-4 text-left transition-smooth border-b border-solid ${rowSurfaceClass} ${
            !canExpand ? 'cursor-default' : 'cursor-pointer'
          }`}
        >
          <div className={`font-sans text-sm capitalize text-center ${primaryTextClass}`}>
            {entry.rank}
          </div>
          <div
            className={`font-sans text-sm truncate ${
              isWinner || isHighlighted
                ? `${primaryTextClass} font-extrabold`
                : 'text-white font-normal'
            }`}
          >
            {entry.username}
          </div>
          <div className="text-center">
            {prizeLabel ? (
              <span className={`font-sans text-sm font-semibold capitalize ${prizeTextClass}`}>
                {prizeLabel}
              </span>
            ) : null}
          </div>
          <div className={`font-sans text-sm capitalize text-center ${primaryTextClass}`}>
            {entry.points}
          </div>
          <div className={`flex items-center justify-center ${chevronClass}`}>
            {canExpand ? (
              isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )
            ) : null}
          </div>
        </button>

        {isExpanded && renderExpanded?.(entry, isCurrentUser, isPinned)}
      </div>
    );
  };

  return (
    <div data-test-id={dataTestId} className={className}>
      {isLoading && (
        <div className="p-8 text-center">
          <div className="text-muted-foreground font-sans text-sm">{resolvedLoadingLabel}</div>
        </div>
      )}

      {error && (
        <div className="p-8">
          <EmptyState title={resolvedErrorTitle} showAdSpace={false} />
        </div>
      )}

      {!isLoading && !error && entries.length === 0 && (
        <div className="p-8">
          <EmptyState title={resolvedEmptyTitle} showAdSpace={false} />
        </div>
      )}

      {!isLoading && !error && orderedEntries.length > 0 && (
        <>
          <div
            ref={tableTopRef}
            className={`${ROW_GRID} mb-2 font-sans text-xs capitalize text-white`}
          >
            <div className="text-center">{t('leaderboard.rank')}</div>
            <div>{t('leaderboard.player')}</div>
            <div className="text-center">{t('leaderboard.prize')}</div>
            <div className="text-center">{t('leaderboard.points')}</div>
            <div />
          </div>

          <div>
            {currentUserEntry && currentUserEntry.rank !== 1 && (
              <>
                {renderRow(currentUserEntry, true, true)}
                <div className="h-0" aria-hidden />
              </>
            )}

            {orderedEntries.map(entry =>
              renderRow(entry, !!currentUserId && entry.user_id === currentUserId, false)
            )}
          </div>

          <div
            className={`flex w-full items-center gap-4 px-6 pb-8 pt-4 shadow-[0px_4px_16px_rgba(18,18,18,0.1)] ${
              hasNextPage ? '' : 'justify-end'
            }`}
          >
            {hasNextPage && onLoadMore && (
              <Button
                type="button"
                variant="secondary"
                shape="pill"
                size="lg"
                className="min-w-0 flex-1"
                loading={isFetchingNextPage}
                onClick={onLoadMore}
                data-test-id="leaderboard-load-more"
              >
                {t('leaderboard.loadMore')}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              shape="pill"
              size="lg"
              className="size-12 shrink-0 p-3"
              onClick={scrollToTop}
              aria-label={t('leaderboard.scrollToTop')}
              data-test-id="leaderboard-scroll-top"
            >
              <ArrowUp className="!size-6" strokeWidth={1.5} aria-hidden />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default LeaderboardTable;
